import { Injectable } from '@angular/core';
import { ApiService, Pagination, PER_PAGE } from './api.service';
import { BehaviorSubject, Observable, shareReplay, Subject, switchMap, take } from 'rxjs';
import { Paginate } from '@shared/entities/paginate';
import { CompanyGroup } from '@shared/entities/companyGroup';

export interface CompanyGroupFilterURL {
  id?: string;
  name?: string;
  active?: boolean;
  page?: number;
  size?: number;
}

@Injectable({
  providedIn: 'root'
})
export class CompanyGroupService extends ApiService<CompanyGroup> {
  private _refresh$: Subject<Pagination>;
  allCompanyGroups$: Observable<Paginate<CompanyGroup>>;
  private _currentPaginator: Pagination;
  private _currentFilters: CompanyGroupFilterURL = {};

  set filters(value: CompanyGroupFilterURL) {
    this._currentFilters = value;
    this.refresh(1, PER_PAGE);
  }

  constructor() {
    super(CompanyGroup, '/company-groups', '/company-groups');
    this._currentPaginator = { page: 1, perPage: PER_PAGE };
    this._refresh$ = new BehaviorSubject<Pagination>(this._currentPaginator);
    this.allCompanyGroups$ = this._refresh$.pipe(
      switchMap((pagination) => {
        if (this._currentFilters.name) {
          this.setUrlParameter('name', this._currentFilters.name);
        } else {
          this.removeUrlParameter('name');
        }
        if (this._currentFilters.active !== undefined) {
          this.setUrlParameter('active', this._currentFilters.active.toString());
        } else {
          this.removeUrlParameter('active');
        }
        return this.paginate(CompanyGroup, pagination.page, pagination.perPage);
      }),
      shareReplay(1)
    );
  }

  refresh(page: number, perPage: number = PER_PAGE) {
    this._currentPaginator = { page, perPage };
    this._refresh$.next(this._currentPaginator);
    return this.allCompanyGroups$.pipe(take(1));
  }

  refreshFilters() {
    this._currentFilters = {};
    this.refresh(1, PER_PAGE);
  }

  saveCompanyGroup(group: CompanyGroup): Observable<CompanyGroup> {
    return this.save(group);
  }

  editCompanyGroup(group: CompanyGroup, id: string): Observable<CompanyGroup> {
    return this.save(group, id);
  }

  deleteCompanyGroup(id: string): Observable<boolean> {
    this.setUrlParameter('id', id);
    return this.remove(id);
  }

  findByFilter(id: string): Observable<Paginate<CompanyGroup>> {
    this.setUrlParameter('id', id);
    return this.paginate(CompanyGroup, 1, 1);
  }
}
